import { authExchange as urqlAuthExchange } from "@urql/exchange-auth";
import { jwtDecode } from "jwt-decode";
import type { Operation } from "urql";
import { isDemoMode } from "@/config";

// demo環境で使い回す固定トークン
const DEMO_TOKEN = "demo" as const;

type BasicJwtPayload = {
  exp?: number;
};

// トークン期限のズレ吸収用に30秒の猶予を持たせる
const TOKEN_EXPIRY_LEEWAY_SECONDS = 30;

/**
 * JWTのexpクレームを安全に取り出します。
 */
const extractTokenExpiry = (token: string): number | null => {
  try {
    const payload = jwtDecode<BasicJwtPayload>(token);
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
};

/**
 * 猶予時間込みでトークンが失効済みかどうかを判定します。
 */
const isTokenExpired = (token: string): boolean => {
  const exp = extractTokenExpiry(token);
  if (typeof exp !== "number") {
    return true;
  }
  const nowSeconds = Math.floor(Date.now() / 1000);
  return exp <= nowSeconds + TOKEN_EXPIRY_LEEWAY_SECONDS;
};

/**
 * Operation contextに格納されたトークンを利用可能な形に整えます。
 *
 * @param authToken Operation contextから渡された値。
 * @returns 利用可能なBearerトークン。無効な場合はnull。
 */
const readToken = (authToken: unknown): string | null => {
  if (isDemoMode) {
    return DEMO_TOKEN;
  }
  const token = typeof authToken === "string" ? authToken : null;
  if (!token) {
    return null;
  }
  return isTokenExpired(token) ? null : token;
};

/**
 * Operation context にセットされたトークンを送信前に更新すべきか判定します。
 * 未設定の場合は API の認証応答に委ねます。
 */
export const shouldRefreshAuthBeforeOperation = (
  authToken: unknown,
): boolean => {
  if (isDemoMode) {
    return false;
  }
  const token = typeof authToken === "string" ? authToken : null;
  return token ? isTokenExpired(token) : false;
};

/**
 * fetchOptions で独自の Authorization ヘッダーが指定されているか判定します。
 */
const hasCustomAuthorizationHeader = (operation: Operation): boolean => {
  const fetchOptions =
    typeof operation.context.fetchOptions === "function"
      ? operation.context.fetchOptions()
      : operation.context.fetchOptions;
  const headers = fetchOptions?.headers;
  if (!headers) {
    return false;
  }
  if (headers instanceof Headers) {
    return Boolean(headers.get("Authorization"));
  }
  if (Array.isArray(headers)) {
    return headers.some(([key]) => key.toLowerCase() === "authorization");
  }
  if (typeof headers === "object") {
    return Object.keys(headers).some(
      (key) => key.toLowerCase() === "authorization",
    );
  }
  return false;
};

/**
 * authRedirectExchange に401ステータスを伝搬させるためのカスタムエラー。
 */
class TokenInvalidError extends Error {
  status = 401;
  statusCode = 401;

  constructor(message = "Token invalid or expired") {
    super(message);
    this.name = "TokenInvalidError";
  }
}

/**
 * サーバー/クライアント共通の認証 exchange。
 * @see https://www.npmjs.com/package/%40urql/exchange-auth
 */
export const authExchange = urqlAuthExchange(async (utils) => ({
  /**
   * 各 Operation に Authorization ヘッダーを付与します。
   * 無効トークンの場合は何もせず透過させます。
   */
  addAuthToOperation(operation) {
    if (hasCustomAuthorizationHeader(operation)) {
      return operation;
    }
    const token = readToken(operation.context.authToken);
    if (!token) {
      return operation;
    }
    return utils.appendHeaders(operation, {
      Authorization: `Bearer ${token}`,
    });
  },
  /**
   * リクエスト送信前にトークンの有効性を確認し、失効済みなら再試行フローへ移行させます。
   */
  willAuthError(operation) {
    if (hasCustomAuthorizationHeader(operation)) {
      return false;
    }
    return shouldRefreshAuthBeforeOperation(operation.context.authToken);
  },
  didAuthError() {
    // 認証エラーの再送は別exchangeでハンドリングするため常にfalse
    return false;
  },
  /**
   * 有効なトークンが得られない場合は即座に401エラーを投げ、authRedirectExchangeへ委譲します。
   */
  async refreshAuth() {
    throw new TokenInvalidError();
  },
}));
